import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Play, 
    Pause, 
    Eye,
    Headphones,
    ChevronRight,
    X,
    Check
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PrimaryButton, SecondaryButton, IconButton } from './brand/BrandButton';
import { Heading, BodyText, SmallText } from './brand/BrandTypography';

// Simplified speed for quick bilateral - 80 BPM (1.5s per pass) for calming
const QUICK_SPEED = { duration: 1.5, bpm: 80 };

// Music for gentle bilateral
const MUSIC_SRC = '/audio/ocean-peace.mp3';

// Simple visual orb component
const QuickVisualOrb = ({ isActive, onComplete, totalDuration = 30 }) => {
    const [position, setPosition] = useState(50);
    const animationRef = useRef(null);
    const startTimeRef = useRef(null);

    useEffect(() => {
        if (!isActive) {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
            return;
        }

        startTimeRef.current = performance.now();
        const cycleDuration = QUICK_SPEED.duration * 1000;

        const animate = (currentTime) => {
            const elapsed = currentTime - startTimeRef.current;
            const cycleProgress = (elapsed % cycleDuration) / cycleDuration;
            
            // Smooth sine wave movement
            const easeProgress = (Math.sin((cycleProgress - 0.25) * Math.PI * 2) + 1) / 2;
            setPosition(3 + easeProgress * 94); // 3% to 97%

            // Check if total duration reached
            if (elapsed >= totalDuration * 1000) {
                onComplete?.();
                return;
            }

            animationRef.current = requestAnimationFrame(animate);
        };

        animationRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, [isActive, totalDuration, onComplete]);

    return (
        <div className="relative w-full h-32 rounded-2xl bg-gradient-to-b from-[#F5F0E8] to-[#E8E4DC] flex items-center justify-center overflow-hidden">
            {/* Track line */}
            <div className="absolute w-[90%] h-0.5 bg-[#7D8E95]/10" />
            
            {/* Edge markers */}
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-0.5 h-4 rounded-full bg-[#7D8E95]/20" />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-0.5 h-4 rounded-full bg-[#7D8E95]/20" />
            
            {/* Moving orb */}
            <div 
                className="absolute"
                style={{
                    left: `${position}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                }}
            >
                {/* Glow */}
                <div className="absolute -inset-4 rounded-full blur-xl bg-[#1D6B6B]/30" />
                {/* Orb */}
                <div 
                    className="w-10 h-10 rounded-full shadow-lg"
                    style={{ 
                        backgroundColor: '#1D6B6B',
                        boxShadow: '0 0 20px rgba(29, 107, 107, 0.5)'
                    }}
                />
            </div>
        </div>
    );
};

// Music bilateral audio component
const QuickAudioBilateral = ({ isActive, volume = 0.5 }) => {
    const audioContextRef = useRef(null);
    const audioElementRef = useRef(null);
    const sourceNodeRef = useRef(null);
    const pannerRef = useRef(null);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (!isActive) {
            if (audioElementRef.current) {
                audioElementRef.current.pause();
                audioElementRef.current = null;
            }
            if (audioContextRef.current) {
                try { audioContextRef.current.close(); } catch (e) {}
                audioContextRef.current = null;
            }
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            return;
        }

        // Create audio context
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioContext();

        // Load music
        audioElementRef.current = new Audio(MUSIC_SRC);
        audioElementRef.current.loop = true;
        audioElementRef.current.volume = volume;
        
        // Create panner
        sourceNodeRef.current = audioContextRef.current.createMediaElementSource(audioElementRef.current);
        pannerRef.current = audioContextRef.current.createStereoPanner();
        pannerRef.current.pan.setValueAtTime(-0.9, audioContextRef.current.currentTime);
        
        sourceNodeRef.current.connect(pannerRef.current);
        pannerRef.current.connect(audioContextRef.current.destination);
        
        audioElementRef.current.play().catch(console.error);

        // Pan timing
        const halfCycleMs = (QUICK_SPEED.duration * 1000) / 2;
        let side = 'left';

        intervalRef.current = setInterval(() => {
            side = side === 'left' ? 'right' : 'left';
            if (pannerRef.current && audioContextRef.current) {
                pannerRef.current.pan.setTargetAtTime(
                    side === 'left' ? -0.9 : 0.9,
                    audioContextRef.current.currentTime,
                    0.1
                );
            }
        }, halfCycleMs);

        return () => {
            if (audioElementRef.current) audioElementRef.current.pause();
            if (audioContextRef.current) {
                try { audioContextRef.current.close(); } catch (e) {}
            }
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [isActive, volume]);

    return null;
};

// Main Quick Bilateral Component for Micro Reset
export const QuickBilateral = ({ 
    onComplete, 
    onNavigateToFull,
    duration = 30, // 30 seconds by default
    showVisual = true 
}) => {
    const navigate = useNavigate();
    const [isPlaying, setIsPlaying] = useState(false);
    const [showHeadphonePrompt, setShowHeadphonePrompt] = useState(true);
    const [headphoneConfirmed, setHeadphoneConfirmed] = useState(false);
    const [hasCompleted, setHasCompleted] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(duration);
    const timerRef = useRef(null);

    // Timer countdown
    useEffect(() => {
        if (isPlaying && timeRemaining > 0) {
            timerRef.current = setInterval(() => {
                setTimeRemaining(prev => {
                    if (prev <= 1) {
                        setIsPlaying(false);
                        setHasCompleted(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isPlaying, timeRemaining]);

    const handleStart = () => {
        if (!headphoneConfirmed) {
            setShowHeadphonePrompt(true);
            return;
        }
        setIsPlaying(true);
    };

    const handleConfirmHeadphones = () => {
        setHeadphoneConfirmed(true);
        setShowHeadphonePrompt(false);
        setIsPlaying(true);
    };

    const handleComplete = useCallback(() => {
        setIsPlaying(false);
        setHasCompleted(true);
    }, []);

    const handleGoToFullTool = () => {
        navigate('/regulate/emdr');
    };

    // Completion screen
    if (hasCompleted) {
        return (
            <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-[#1D6B6B]" />
                </div>
                <Heading as="h3" size="text-lg" className="mb-2">Nice work</Heading>
                <SmallText className="text-[#7D8E95] mb-4">
                    That was a quick bilateral calming session
                </SmallText>
                
                <div className="space-y-3">
                    <button
                        onClick={handleGoToFullTool}
                        className="w-full p-3 rounded-xl bg-[#1D6B6B]/10 border border-[#1D6B6B]/20 hover:bg-[#1D6B6B]/20 transition-colors text-left"
                        data-testid="go-to-full-emdr"
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Eye className="w-5 h-5 text-[#1D6B6B]" />
                                <div>
                                    <BodyText className="font-medium text-[#1D6B6B]">Want more?</BodyText>
                                    <SmallText className="text-[#7D8E95]">Try the full EMDR/Bilateral Regulation tool</SmallText>
                                </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-[#1D6B6B]" />
                        </div>
                    </button>
                    
                    <SecondaryButton 
                        onClick={onComplete} 
                        className="w-full"
                        data-testid="continue-micro-reset"
                    >
                        Continue
                    </SecondaryButton>
                </div>
            </div>
        );
    }

    // Headphone prompt
    if (showHeadphonePrompt && !headphoneConfirmed) {
        return (
            <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-[#FFBB98]/20 flex items-center justify-center mx-auto mb-4">
                    <Headphones className="w-8 h-8 text-[#1D6B6B]" />
                </div>
                <Heading as="h3" size="text-lg" className="mb-2">Grab your headphones</Heading>
                <SmallText className="text-[#7D8E95] mb-6">
                    This exercise uses gentle sounds that move between your ears to help calm your nervous system. Headphones are needed for the bilateral effect.
                </SmallText>
                
                <div className="space-y-3">
                    <PrimaryButton 
                        onClick={handleConfirmHeadphones}
                        className="w-full"
                        data-testid="confirm-headphones"
                    >
                        <Headphones className="w-4 h-4 mr-2" />
                        I have headphones
                    </PrimaryButton>
                    <button
                        onClick={onComplete}
                        className="text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                    >
                        Skip this exercise
                    </button>
                </div>
            </div>
        );
    }

    // Active session
    return (
        <div className="py-4">
            {/* Visual orb */}
            {showVisual && (
                <div className="mb-4">
                    <QuickVisualOrb 
                        isActive={isPlaying} 
                        onComplete={handleComplete}
                        totalDuration={duration}
                    />
                </div>
            )}
            
            {/* Audio component (invisible) */}
            <QuickAudioBilateral isActive={isPlaying} volume={0.5} />
            
            {/* Instructions */}
            <div className="text-center mb-4">
                {isPlaying ? (
                    <>
                        <SmallText className="text-[#7D8E95] mb-2">
                            Let the gentle sound and movement calm your mind
                        </SmallText>
                        <SmallText className="text-[#1D6B6B] font-medium">
                            {timeRemaining}s remaining
                        </SmallText>
                    </>
                ) : (
                    <SmallText className="text-[#7D8E95]">
                        A gentle bilateral calming moment
                    </SmallText>
                )}
            </div>
            
            {/* Controls */}
            <div className="flex justify-center gap-3">
                {!isPlaying ? (
                    <PrimaryButton 
                        onClick={handleStart}
                        data-testid="start-quick-bilateral"
                    >
                        <Play className="w-4 h-4 mr-2" />
                        Start
                    </PrimaryButton>
                ) : (
                    <SecondaryButton 
                        onClick={() => {
                            setIsPlaying(false);
                            setHasCompleted(true);
                        }}
                        data-testid="stop-quick-bilateral"
                    >
                        <Pause className="w-4 h-4 mr-2" />
                        End early
                    </SecondaryButton>
                )}
            </div>
        </div>
    );
};

export default QuickBilateral;
